// @ts-strict-ignore
import React from 'react'
import { isMapAddressed } from '../collection/tourMapAddress'
import TagManager from 'react-gtm-module'
import slugify from 'slugify'
import { MatomoProvider, createInstance } from '@datapunt/matomo-tracker-react'
import { BASE_PATH, PAGE_TYPES, SCANNER_PAGE_SLUG, SETTINGS_PAGE_SLUG } from '../navigation/routes'
import { loadCollectionDetailsFromDisk, loadCollectionItemsFromDisk } from '../collection/functions'
import { loadScreenFromDisk } from '../screen/functions'
import Settings, { loadAppMetaDataFromDisk } from '../settings/functions'
import { LanguageGate } from '../home/functions'
import {
	AppMetaData,
	Collection,
	CollectionItem,
	COLLECTION_ITEM_SUBTYPE_PANORAMA,
	COLLECTION_ITEM_SUBTYPE_STORY,
	COLLECTION_ITEM_SUBTYPE_TOUR,
	COLLECTION_ITEM_SUBTYPE_WEB,
	getConfiguration,
	Screen,
} from 'mytours-read-common'
import { convertCamelCaseKeysToSnakeCase } from '../platform'
import { History } from 'history'
import waitForItem from './waitForItem'
import { GetCollectionItemsIndexResponseData } from 'mytours-read-api-typescript'
import { isAvailableLanguage } from '../navigation/utils'

// Google Tag Function, declared in views/layouts/mobile_web.html.erb
declare const gtag
// Tracking code, declared in views/layouts/mobile_web.html.erb
declare const googleAnalyticsCode
// Analytics type, declared in views/layouts/mobile_web.html.erb
declare const analyticsType
// Mamoto site id, declared in views/layouts/mobile_web.html.erb
declare const matomoSiteId
// Mamoto collection url, declared in views/layouts/mobile_web.html.erb
declare const matomoCollectionUrl
// Mamoto dimension IDs, declared in views/layouts/mobile_web.html.erb
declare const matomoDimensionIds
// Received from backend, declared in views/layouts/mobile_web.html.erb
declare const projectId: number

// when edit position you are editing dimension id
const analyticsDimensions = [
	'collection',
	'audio_duration',
	'audio_title',
	'audio_current_time',
	'audio_percent',
	'language',
	'method',
	'item_name',
	'screen_section',
]

const GTM = 'gtm'
const GTAG = 'gtag'

// INFO: when you edit the next interface, then you need to edit `analyticsDimensions` dimensions as well
export interface EventParams {
	audioDuration?: number // in seconds
	audioTitle?: string
	audioCurrentTime?: number // in seconds
	audioPercent?: number // in percents

	collection?: string
	collectionId?: number
	language?: string
	method?: 'map' | 'keypad' | 'qr-code' | 'hamburger' | 'search' // for `select_item` event
	itemName?: string // for `select_item` event
	screenSection?: string
	accountId?: number // account_id of the content being viewed (Matomo dimension only); reporting maps it to the guide client

	videoDuration?: number // in seconds
	videoTitle?: string
	videoCurrentTime?: number // in seconds
	videoPercent?: number // in percents
}

export enum EventActionsEnum {
	AudioStart = 'audio_start',
	AudioPause = 'audio_pause',
	AudioProgress = 'audio_progress',
	AudioResume = 'audio_resume',
	AudioComplete = 'audio_complete',
	AudioViewTranscript = 'audio_view_transcript',
	AudioSkipForward = 'audio_skip_forward',
	AudioSkipBackward = 'audio_skip_backward',
	AudioOpenPlayer = 'audio_open_player',
	OpenLightbox = 'open_lightbox',
	OpenMainMenu = 'open_main_menu',
	SelectItem = 'select_item',
	VideoStart = 'video_start',
	VideoPause = 'video_pause',
	VideoProgress = 'video_progress',
	VideoResume = 'video_resume',
	VideoComplete = 'video_complete',
	ChangeLanguage = 'change_language',
}

// set instance in analytics provider
let matomoInstance

// account_id of the content on the page view we last tracked, so events fired while on that page
// (audio, etc.) can be tagged with the same account. Set by formatPageConfigs.
let currentAccountId: number | undefined

// account_id is now exposed on every content show response (collection/screen/...), so we read it
// straight off the loaded content. Typed as unknown because the generated SDK types don't list it yet.
const contentAccountId = (item?: unknown): number | undefined =>
	(item as { account_id?: number } | undefined)?.account_id

export const initAnalytics = () => {
	switch (analyticsType) {
		case GTM: {
			const tagManagerArgs = {
				gtmId: googleAnalyticsCode,
			}

			TagManager.initialize(tagManagerArgs)
			break
		}
		case GTAG: {
			if (typeof gtag === 'undefined') {
				return
			}
			const gtagDimensions = []

			// create Array of dimesions for gtag
			Object.keys(analyticsDimensions).forEach(key => {
				const newKey = 'dimension' + (+key + 1)
				gtagDimensions[newKey] = analyticsDimensions[key]
			})
			// convert Array to Object
			const customMap = Object.assign({}, gtagDimensions)
			gtag('js', new Date())
			gtag('config', googleAnalyticsCode, {
				send_page_view: false,
				custom_map: customMap,
			})
			break
		}
	}
}

// TODO: update matomo to the new events as well
export const sendEvent = (action: EventActionsEnum, params: EventParams) => {
	const EVENT_CATEGORY = 'engagement'

	if (!params.language) {
		params.language = LanguageGate.currentLocale
	}

	switch (analyticsType) {
		case GTM: {
			const tagManagerArgs = {
				dataLayer: {
					event: action,
					event_info: {
						...convertCamelCaseKeysToSnakeCase(params),
					},
					event_category: EVENT_CATEGORY,
				},
			}

			TagManager.dataLayer(tagManagerArgs)
			break
		}
		case GTAG: {
			if (typeof gtag === 'undefined') {
				return
			}
			gtag('event', action, {
				...convertCamelCaseKeysToSnakeCase(params),
				event_category: EVENT_CATEGORY,
				send_to: googleAnalyticsCode,
			})
			break
		}
	}

	if (matomoSiteId != null && matomoInstance) {
		if (params.accountId == null) {
			params.accountId = currentAccountId
		}
		matomoInstance.trackEvent({
			category: EVENT_CATEGORY,
			action,
			customDimensions: getMatomoCustomDimensions(params),
		})
	}
}

const getMatomoCustomDimensions = (params: EventParams) => {
	const convertedParams = convertCamelCaseKeysToSnakeCase(params)
	// replace dimension array to matomo structure
	const customDimensions = Object.entries(matomoDimensionIds)
		.map(([dimension, id]) => {
			if (dimension === 'device_platform') {
				const platform = getConfiguration().getPlatform()
				return {
					id: id,
					value: platform,
				}
			}

			if (dimension === 'collection') {
				const collection = convertedParams.collection
				const collectionId = convertedParams.collection_id
				return {
					id: id,
					value: collection && collectionId ? `${collectionId}/${getSlug(collection)}` : undefined,
				}
			}

			if (dimension === 'account_id') {
				return {
					id,
					value: convertedParams.account_id != null ? String(convertedParams.account_id) : undefined,
				}
			}

			return {
				id,
				value: convertedParams[dimension],
			}
		})
		// id can be null when a dimension slot has not been provisioned in Matomo yet (e.g. account_id
		// before sync_matomo_dimensions has run for this site) - skip those.
		.filter(item => !!item.id && !!item.value)

	// Need to set collection to empty or else the previous collection will be send
	if (matomoDimensionIds.collection && !params.collection) {
		// https://github.com/jonkoops/matomo-tracker/issues/413 don't handle one time custom dimension so we interact with matomo directly
		// @ts-ignore _paq is part of matomo: https://developer.matomo.org/guides/tracking-javascript-guide
		_paq.push(['deleteCustomDimension', matomoDimensionIds.collection])
	}

	// Same as collection above: clear a stale account_id so it isn't carried into a content-less
	// page/event (settings, scanner, home).
	if (matomoDimensionIds.account_id && params.accountId == null) {
		// @ts-expect-error _paq is part of matomo: https://developer.matomo.org/guides/tracking-javascript-guide
		_paq.push(['deleteCustomDimension', matomoDimensionIds.account_id])
	}

	return customDimensions
}

export const sendPathname = (pathname: string, state?: History.LocationState, hash?: string, search?: string) => {
	const waitingInterval = setInterval(() => {
		// check if page and language loaded
		if (LanguageGate.currentLocale) {
			clearInterval(waitingInterval)
			parsePath(pathname, state, hash, search)
		}
	}, 100)
}

interface Props {
	children: JSX.Element
}
export const AnalyticsProvider: React.FC<Props> = props => {
	if (matomoSiteId) {
		matomoInstance = createInstance({
			urlBase: matomoCollectionUrl,
			siteId: matomoSiteId,
			trackerUrl: matomoCollectionUrl + '/matomo.php',
			srcUrl: matomoCollectionUrl + '/matomo.js',
		})

		return <MatomoProvider value={matomoInstance}>{props.children}</MatomoProvider>
	}

	return props.children
}

const formatPageConfigs = (
	{
		location,
		title,
		matomoTitle,
	}: {
		location: string
		title: string
		matomoTitle?: string
	},
	collection?: string,
	collectionId?: number,
	accountId?: number,
) => {
	// Remember the content's account for events fired on this page; undefined on content-less pages.
	currentAccountId = accountId
	switch (analyticsType) {
		case GTM: {
			const tagManagerArgs: any = {
				dataLayer: {
					event: 'pageview',
					page: {
						page_location: window.location.origin + location,
						page_title: title,
						language: LanguageGate.currentLocale,
					},
				},
			}

			if (collection) {
				tagManagerArgs.dataLayer.page.collection = collection
			}

			TagManager.dataLayer(tagManagerArgs)
			break
		}
		case GTAG: {
			if (typeof gtag === 'undefined') {
				return
			}
			const eventData: any = {
				page_location: window.location.origin + location,
				page_title: title,
				language: LanguageGate.currentLocale,
				send_to: googleAnalyticsCode,
			}

			if (collection) {
				eventData.collection = collection
			}

			gtag('event', 'page_view', eventData)
			break
		}
	}

	if (matomoSiteId != null && matomoInstance) {
		matomoInstance.trackPageView({
			href: `https://${projectId}.stqry.app${location}`,
			documentTitle: matomoTitle || title,
			customDimensions: getMatomoCustomDimensions({
				language: LanguageGate.currentLocale,
				collection,
				collectionId,
				accountId,
			}),
		})
	}
}

const getTabParams = (params: any[], index: number) => {
	let tabIndex, page, generalPage

	if (!params[index]) {
		tabIndex = 0
	} else if (!isNaN(Number(params[index]))) {
		tabIndex = Number(params[index]) - 1
	} else if ([SCANNER_PAGE_SLUG, SETTINGS_PAGE_SLUG].includes(params[index])) {
		generalPage = params[index]
	} else if (PAGE_TYPES.includes(params[index])) {
		page = params[index]
	} else if (isAvailableLanguage(params[index]) || Settings.appLanguage.supportedLanguages.includes(params[index])) {
		return getTabParams(params, index + 1)
	}

	return {
		tabIndex,
		page,
		index,
		generalPage,
	}
}

const getSlug = (name: string) => slugify(name || 'no-name', { lower: true })

const formatPage = async ({
	page,
	id,
	collectionId,
	insideTour,
	hash,
	search,
}: {
	page: any
	id: number
	collectionId?: number
	insideTour?: boolean
	hash?: string
	search?: string
}) => {
	let collectionName: string
	let collectionDetails: Collection
	if (collectionId) {
		collectionDetails = await waitForItem<Collection>(
			async () =>
				await loadCollectionDetailsFromDisk(String(projectId), collectionId, null, LanguageGate.currentLocale),
		)
		if (collectionDetails) {
			collectionName = collectionDetails.name
		}
	}

	if (page === COLLECTION_ITEM_SUBTYPE_TOUR) {
		const tourDetails = await waitForItem<Collection>(
			async () => await loadCollectionDetailsFromDisk(String(projectId), id, null, LanguageGate.currentLocale),
		)

		if (tourDetails) {
			const { name } = tourDetails

			formatPageConfigs(
				{
					location: `/tour/${id}/${getSlug(name)}/tour-overview`,
					title: `${name} - Tour Overview`,
					matomoTitle: `Tour - ${name} - Tour Overview`,
				},
				name,
				id,
				contentAccountId(tourDetails),
			)
		}
	} else if (
		[COLLECTION_ITEM_SUBTYPE_STORY, COLLECTION_ITEM_SUBTYPE_WEB, COLLECTION_ITEM_SUBTYPE_PANORAMA].includes(page) ||
		page === 'screen'
	) {
		if (insideTour && (collectionId || !id)) {
			const collectionItems = await waitForItem<CollectionItem[]>(
				async () =>
					await loadCollectionItemsFromDisk(String(projectId), collectionId, null, LanguageGate.currentLocale),
			)

			const searchParams = new URLSearchParams(search)
			const showDetailsModal = searchParams.get('show-detail-modal') === 'true'

			// Handle event when map oppened in tour stops view. Only considered as map if we don't show the details modal
			if (
				collectionName &&
				(isMapAddressed(hash, search) || collectionDetails.initial_layout === 'map') &&
				!showDetailsModal
			) {
				formatPageConfigs(
					{
						location: `/tour/${collectionDetails.id}/${getSlug(collectionDetails.name)}/map`,
						title: `${collectionDetails.name} - Map`,
						matomoTitle: `Tour - ${collectionDetails.name} - Map`,
					},
					collectionDetails.name,
					collectionDetails.id,
					contentAccountId(collectionDetails),
				)
			} else if (collectionItems) {
				const collectionItem = id
					? collectionItems.find(item => {
							// if url includes story, web, panorama then use item_id instead id
							if (
								[COLLECTION_ITEM_SUBTYPE_STORY, COLLECTION_ITEM_SUBTYPE_WEB, COLLECTION_ITEM_SUBTYPE_PANORAMA].includes(
									page,
								)
							) {
								return item.item_id === id
							}

							return item.id === id
						})
					: collectionItems[0]

				if (collectionItem) {
					const { name, item_id: screenId } = collectionItem

					formatPageConfigs(
						{
							location: `/screen/${screenId}/${getSlug(name)}`,
							title: `Screen - ${name}`,
						},
						collectionName,
						collectionId,
						contentAccountId(collectionDetails),
					)
				}
			}
		} else {
			const screen = await waitForItem<Screen>(
				async () => await loadScreenFromDisk(String(projectId), id, null, LanguageGate.currentLocale),
			)

			if (screen) {
				const { name } = screen

				formatPageConfigs(
					{
						location: `/screen/${id}/${getSlug(name)}`,
						title: `Screen - ${name}`,
					},
					collectionName,
					collectionId,
					contentAccountId(screen),
				)
			}
		}
	} else {
		const collectionDetails = await waitForItem<Collection>(
			async () => await loadCollectionDetailsFromDisk(String(projectId), id, null, LanguageGate.currentLocale),
		)

		if (collectionDetails) {
			const { name } = collectionDetails
			const isMapView = isMapAddressed(hash, search)

			// handle event when map oppened in collection view
			formatPageConfigs(
				{
					location: `/${page}/${id}/${getSlug(name)}` + (isMapView ? '/map' : ''),
					title: `${name} - ${page}` + (isMapView ? ' - Map' : ''),
					matomoTitle: `${page[0].toUpperCase() + page.slice(1)} - ${name}` + (isMapView ? ' - Map' : ''),
				},
				name,
				id,
				contentAccountId(collectionDetails),
			)
		}
	}
}

const parsePath = async (pathname: string, state: History.LocationState, hash?: string, search?: string) => {
	// Remove basepath from pathname to split only parameters
	if (BASE_PATH) {
		pathname = pathname.replace(BASE_PATH, '')
	}

	// TODO: Property 'parentItemId' does not exist on type 'unknown'
	const parentItemId = (state && (state as any)?.parentItemId) || undefined
	const params = pathname.split('/')

	const searchParams = new URLSearchParams(search)
	if (searchParams.has('m')) {
		const modalWindowsParams = searchParams.getAll('m')
		const modalWindowsParam = modalWindowsParams[modalWindowsParams.length - 1]
		const [type, id] = modalWindowsParam.split(';')
		const subtype = type.split('_')[1]

		// check if the second parameter is NOT number
		// it means that second parameter is language code
		const firstIndex = isNaN(Number(params[1])) ? 2 : 1

		params[firstIndex + 1] = subtype
		params[firstIndex + 2] = id
		params.length = firstIndex + 3
	}

	const { tabIndex, page, index, generalPage } = getTabParams(params, 1)

	if (generalPage && params[index]) {
		// hardcoded pages, like scanner and settings

		formatPageConfigs({
			location: `/${params[index]}`,
			title: params[index].charAt(0).toUpperCase() + params[index].slice(1),
		})
	} else if (page && params[index + 1]) {
		if (params[index + 2]) {
			const page =
				params[index] === 'list' &&
				[COLLECTION_ITEM_SUBTYPE_STORY, COLLECTION_ITEM_SUBTYPE_WEB, COLLECTION_ITEM_SUBTYPE_PANORAMA].includes(
					params[index + 2] as GetCollectionItemsIndexResponseData.ItemSubtypeEnum,
				)
					? params[index + 2]
					: 'screen'

			formatPage({
				// means that we have only screens inside tour
				page,
				id: Number(params[index + 3]),
				collectionId: Number(params[index + 1]),
				insideTour: true,
				hash,
				search,
			})
		} else {
			formatPage({
				page,
				id: Number(params[index + 1]),
				collectionId: parentItemId,
				hash,
				search,
			})
		}
	} else {
		// Handle items (screens or tours) of default lists and items of tours (both default and map initial layout): `${subdomain}.stqry.app/${tabIndex}/${'tour' || 'list'}/...`
		// Can be tour or list
		if (params[index + 1]) {
			if (params[index + 3]) {
				// Screen inside default list
				formatPage({
					// means that we have only screens inside tour
					page: params[index + 3] === 'item' ? 'screen' : params[index + 3],
					id: Number(params[index + 4]),
					collectionId: Number(params[index + 2]),
					insideTour: true,
					hash,
					search,
				})
			} else {
				// Tour overview inside default list
				formatPage({
					page: params[index + 1],
					id: Number(params[index + 2]),
					collectionId: parentItemId,
					hash,
					search,
				})
			}
		} else {
			// Handle tabs: `${subdomain}.stqry.app/${tabIndex}`
			// TODO: add subtype to `AppTab` interface
			const appData: any = await waitForItem<AppMetaData>(async () => await loadAppMetaDataFromDisk(String(projectId)))

			if (!appData) {
				return
			}

			const { tabs } = appData

			if (tabs && tabs.length === 1) {
				// If we don't have tabs, only single page
				formatPage({
					page: tabs[0].subtype,
					id: tabs[0].tab_type === 'collection' ? tabs[0].collection_id : tabs[0].screen_id,
					hash,
					search,
				})
			} else if (tabs && tabs.length > 1) {
				let name
				let collectionDetails
				let tabAccountId: number | undefined
				let type: string | undefined
				let id: number | undefined
				if (tabs[tabIndex] && tabs[tabIndex].tab_type === 'collection') {
					collectionDetails = await waitForItem<Collection>(
						async () =>
							await loadCollectionDetailsFromDisk(
								String(projectId),
								tabs[tabIndex].collection_id,
								tabs[tabIndex].version,
								LanguageGate.currentLocale,
							),
					)

					if (collectionDetails) {
						name = collectionDetails.name
						type = tabs[tabIndex].subtype
						id = tabs[tabIndex].collection_id
						tabAccountId = contentAccountId(collectionDetails)
					}
				} else if (tabs[tabIndex] && tabs[tabIndex].tab_type === 'screen') {
					const screen = await waitForItem<Screen>(
						async () =>
							await loadScreenFromDisk(
								String(projectId),
								tabs[tabIndex].screen_id,
								tabs[tabIndex].version,
								LanguageGate.currentLocale,
							),
					)

					if (screen) {
						name = screen.name
						type = 'screen'
						id = tabs[tabIndex].screen_id
						tabAccountId = contentAccountId(screen)
					}
				}

				if (name) {
					const capitalizedType = type ? `${type[0].toUpperCase() + type.slice(1)} - ` : ''
					const showDetailsModal = searchParams.get('show-detail-modal') === 'true'
					const collectionItemId = Number(searchParams.get('collection-item-id'))

					// Tabs that contain lists (both default and map initial layout)
					// Items of tabs that contain list with map layout go here as well
					if (tabs[tabIndex].tab_type === 'collection' && type === 'list' && collectionDetails) {
						// Handle event when map oppened in collection view inside tabs root. Only considered as map if we don't show the details modal
						if ((isMapAddressed(hash, search) || collectionDetails.initial_layout === 'map') && !showDetailsModal) {
							formatPageConfigs(
								{
									location: `/list/${collectionDetails.id}/${getSlug(name)}/map`,
									title: name + ' - Map',
									matomoTitle: `${capitalizedType}${name} - Map`,
								},
								name,
								collectionDetails.id,
								tabAccountId,
							)
						} else if (collectionItemId) {
							const collectionItems = await waitForItem<CollectionItem[]>(
								async () =>
									await loadCollectionItemsFromDisk(
										String(projectId),
										collectionDetails.id,
										null,
										LanguageGate.currentLocale,
									),
							)

							if (!collectionItems) {
								return
							}

							const collectionItem = collectionItems.find(item => {
								return item.id === collectionItemId
							})

							if (!collectionItem) {
								return
							}

							const collectionItemType = collectionItem.item_type === 'screen' ? 'screen' : collectionItem.item_subtype
							const capitalizedType = collectionItemType
								? `${collectionItemType[0].toUpperCase() + collectionItemType.slice(1)} - `
								: ''

							formatPageConfigs(
								{
									location: `/${collectionItemType}/${collectionItem.item_id}/${getSlug(collectionItem.name)}`,
									title: name,
									matomoTitle: `${capitalizedType}${collectionItem.name}`,
								},
								name,
								collectionDetails.id,
								tabAccountId,
							)
						} else {
							// List come here
							formatPageConfigs(
								{
									location: `/list/${collectionDetails.id}/${getSlug(name)}`,
									title: name,
									matomoTitle: `${capitalizedType}${name}`,
								},
								undefined,
								undefined,
								tabAccountId,
							)
						}
					} else {
						// Tabs that contain tour overviews or screens
						const isTour = type === 'tour'
						formatPageConfigs(
							{
								location: `${type ? `/${type}` : ''}${id ? `/${id}` : ''}/${getSlug(name)}${
									isTour ? '/tour-overview' : ''
								}`,
								title: name,
								matomoTitle: `${capitalizedType}${name}${isTour ? ' - Tour Overview' : ''}`,
							},
							undefined,
							undefined,
							tabAccountId,
						)
					}
				}
			}
		}
	}
}
